import { FormTemplate, FormField } from '../types/formBuilder';

/** Egy beküldés kinyert kapcsolati adatai + olvasható válaszlista (a szerveroldali extractFormContact tükre). */
export interface SubmissionContact {
  name: string;
  email: string;
  phone: string;
  company: string;
  /** [címke, érték] párok a sablon mezősorrendjében */
  rows: { label: string; value: string; type?: string; raw?: any }[];
  summary: string;
}

const pick = (v: any): string => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(pick).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    if (typeof v.toDate === 'function') return v.toDate().toLocaleString('hu-HU');
    if (v.formatted) return String(v.formatted);
    if (v.url && v.name) return String(v.name);
    return Object.values(v).filter(x => typeof x === 'string').join(' ');
  }
  if (typeof v === 'boolean') return v ? 'Igen' : 'Nem';
  return String(v);
};
const norm = (x: any) => String(x || '').toLowerCase();

export function extractSubmissionContact(template: FormTemplate | null | undefined, data: Record<string, any> | undefined): SubmissionContact {
  const fields: FormField[] = Array.isArray(template?.fields) ? template!.fields : [];
  const d = data || {};
  const out: SubmissionContact = { name: '', email: '', phone: '', company: '', rows: [], summary: '' };
  const parts = { firstName: '', lastName: '' };

  for (const f of fields) {
    const raw = d[f.id] ?? d[f.name];
    if (raw == null || raw === '') continue;
    const v = pick(raw);
    const t = norm(f.type);
    const n = norm(f.name) + ' ' + norm(f.label);
    const label = f.label || f.name || f.id;
    if (t === 'signature' || t === 'file' || t === 'image') { out.rows.push({ label, value: t === 'signature' ? '[aláírás]' : '[fájl]', type: t, raw }); continue; }
    out.rows.push({ label, value: v, type: t, raw });
    if (!out.email && (t === 'email' || /e-?mail/.test(n)) && /@/.test(v)) out.email = v.trim();
    else if (!out.phone && (t === 'phone' || /telefon|phone|mobil/.test(n))) out.phone = v.trim();
    else if (!out.company && /cég|company|vállalkoz|szervezet/.test(n)) out.company = v.trim();
    else if (!parts.lastName && /vezetéknév|lastname|last name/.test(n)) parts.lastName = v.trim();
    else if (!parts.firstName && /keresztnév|firstname|first name/.test(n)) parts.firstName = v.trim();
    else if (!out.name && /^(név|name|teljes név|full name)$/.test(norm(f.label))) out.name = v.trim();
    else if (!out.name && /(^|\s)(név|name)(\s|$)/.test(n) && !/cég|company|termék|product/.test(n)) out.name = v.trim();
  }
  if (!out.name && (parts.firstName || parts.lastName)) out.name = [parts.lastName, parts.firstName].filter(Boolean).join(' ');

  const known = new Set(fields.flatMap(f => [f.id, f.name]));
  for (const [k, v] of Object.entries(d)) {
    if (known.has(k) || v == null || v === '') continue;
    out.rows.push({ label: k, value: pick(v), raw: v });
  }
  if (!out.email) {
    const anyMail = Object.values(d).find(v => typeof v === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())) as string | undefined;
    if (anyMail) out.email = anyMail.trim();
  }
  out.summary = out.rows.map(r => `${r.label}: ${r.value}`).join('\n');
  return out;
}

/** Név + vezeték-/keresztnév bontás ügyfél létrehozáshoz. */
export function splitName(full: string): { firstName: string; lastName: string } {
  const p = (full || '').trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return { firstName: '', lastName: '' };
  if (p.length === 1) return { firstName: p[0], lastName: '' };
  return { lastName: p[0], firstName: p.slice(1).join(' ') };
}

/** CSV export a sablon mezői szerinti oszlopokkal (Excel-barát: BOM + pontosvessző). */
export function submissionsToCsv(template: FormTemplate | null, submissions: { id: string; createdAt: any; status: string; submittedByName?: string; data: Record<string, any> }[]): string {
  const fields = template?.fields || [];
  const cols = fields.length ? fields.map(f => ({ key: f.id, alt: f.name, label: f.label || f.name })) : Array.from(new Set(submissions.flatMap(s => Object.keys(s.data || {})))).map(k => ({ key: k, alt: k, label: k }));
  const esc = (v: any) => { const s = pick(v).replace(/"/g, '""'); return /[;"\n]/.test(s) ? `"${s}"` : s; };
  const head = ['Beküldve', 'Állapot', 'Beküldő', ...cols.map(c => c.label)].map(esc).join(';');
  const lines = submissions.map(s => {
    const d = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
    return [isNaN(d.getTime()) ? '' : d.toLocaleString('hu-HU'), s.status, s.submittedByName || '', ...cols.map(c => (s.data || {})[c.key] ?? (s.data || {})[c.alt] ?? '')].map(esc).join(';');
  });
  return '﻿' + [head, ...lines].join('\n');
}
